// frontend/src/Components/Tasks/TaskListItem.jsx
//
// One row in the dashboard Tasks list. Compact: status badge, type icon,
// description, patient/assignee chips, "new activity" dot, last-activity
// timestamp, comment count, quick-done checkbox.
//
// Layout — left to right:
//   • type icon (microphone for review, sparkles for AI)
//   • description column (badges + description text + patient/practitioner)
//   • right column (timestamp, comment count, mark-done checkbox)
//
// Click row → opens TaskDetailModal. Click checkbox → marks DONE without
// opening the modal. The row disappears as soon as the parent refresh runs;
// the fly-to-Done animation handles the "where did it go" question on its own.

import React, { useRef, useState } from 'react';
import { Badge, Form, Stack } from 'react-bootstrap';
import { patchTaskStatus } from '../../api';

function relativeTime(when) {
  if (!when) return '';
  const ms = Date.now() - new Date(when).getTime();
  if (Number.isNaN(ms)) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(when).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function statusVariant(status) {
  if (status === 'NEW') return 'primary';
  if (status === 'IN_PROGRESS') return 'warning';
  if (status === 'DONE') return 'success';
  return 'secondary';
}

// JIRA status badge props. Per-name override beats the category-based variant
// when a specific JIRA status warrants its own colour (so the dashboard can
// signal "ball is in the customer's court" at a glance).
function jiraStatusBadgeProps(jiraStatus, internalStatus) {
  if (/^waiting for customer$/i.test(jiraStatus || '')) {
    return { bg: '', style: { backgroundColor: '#20c997', color: '#fff' } }; // Bootstrap teal
  }
  return { bg: statusVariant(internalStatus) };
}

function statusLabel(status) {
  if (status === 'IN_PROGRESS') return 'In Progress';
  if (status === 'NEW') return 'To Do';
  if (status === 'DONE') return 'Done';
  return status;
}

// Priority palette — render as inline style so we can use orange for High
// (Bootstrap doesn't ship an "orange" variant). Returns props ready to spread
// onto a <Badge>: { bg: 'none', style, text } where text controls contrast.
function priorityBadgeProps(p) {
  const v = String(p || '').toLowerCase();
  if (v === 'highest') return { style: { backgroundColor: '#dc3545', color: '#fff' } }; // red
  if (v === 'high')    return { style: { backgroundColor: '#fd7e14', color: '#fff' } }; // orange
  if (v === 'medium')  return { style: { backgroundColor: '#ffc107', color: '#212529' } }; // yellow
  if (v === 'low' || v === 'lowest') return { style: { backgroundColor: '#0d6efd', color: '#fff' } }; // blue
  return { style: { backgroundColor: '#6c757d', color: '#fff' } };
}

// Spawn a transient "✓ Done" pill that flies from the row's checkbox to the
// Done filter button — visual confirmation that the task is being placed in
// the Done bucket. Imperative DOM (appended to body) so the element survives
// the row's later unmount when the parent refresh removes it from non-Done
// views.
function flyToDone(sourceRect, targetEl) {
  if (!sourceRect || !targetEl || typeof targetEl.getBoundingClientRect !== 'function') {
    return;
  }
  const targetRect = targetEl.getBoundingClientRect();

  const flyEl = document.createElement('div');
  flyEl.setAttribute('aria-hidden', 'true');
  flyEl.textContent = '';
  Object.assign(flyEl.style, {
    position: 'fixed',
    left: `${sourceRect.left + sourceRect.width / 2}px`,
    top: `${sourceRect.top + sourceRect.height / 2}px`,
    transform: 'translate(-50%, -50%) scale(1)',
    padding: '0.25rem 0.7rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#fff',
    background: '#198754',
    borderRadius: '999px',
    boxShadow: '0 4px 12px rgba(25, 135, 84, 0.35)',
    pointerEvents: 'none',
    zIndex: '2000',
    whiteSpace: 'nowrap',
    opacity: '1',
  });
  flyEl.innerHTML = '<i class="fa-solid fa-check me-1"></i>Done';
  document.body.appendChild(flyEl);

  const dx =
    (targetRect.left + targetRect.width / 2) -
    (sourceRect.left + sourceRect.width / 2);
  const dy =
    (targetRect.top + targetRect.height / 2) -
    (sourceRect.top + sourceRect.height / 2);

  const anim = flyEl.animate(
    [
      { transform: 'translate(-50%, -50%) scale(1)', opacity: 1, offset: 0 },
      { transform: `translate(calc(-50% + ${dx * 0.6}px), calc(-50% + ${dy * 0.6 - 30}px)) scale(1.05)`, opacity: 1, offset: 0.6 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.4)`, opacity: 0, offset: 1 },
    ],
    { duration: 700, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
  );

  const cleanup = () => { try { flyEl.remove(); } catch (_) {} };
  if (!anim) { cleanup(); return; }
  anim.onfinish = () => {
    cleanup();
    // Pulse the Done button so it visibly "receives" the task.
    if (typeof targetEl.animate === 'function') {
      targetEl.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.18)' },
          { transform: 'scale(1)' },
        ],
        { duration: 320, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
    }
  };
  anim.oncancel = cleanup;
  // Safety net — if the animation events never fire (tab backgrounded, etc.),
  // ensure the floating pill is reaped.
  setTimeout(cleanup, 1500);
}

export default function TaskListItem({ task, currentUserId, onOpen, onChanged, getDoneButtonEl }) {
  const [completing, setCompleting] = useState(false);
  // Optimistic-DONE flip — pill + strikethrough switch to "Done" before the
  // server confirms. Reverted on failure. The row exits the list as soon as
  // the parent refresh runs; the fly-to-Done pill animation handles the
  // visual "where did it go" cue.
  const [optimisticDone, setOptimisticDone] = useState(false);
  const checkboxRef = useRef(null);

  if (!task) return null;
  const isAi = task.taskType === 'AI_FOLLOW_UP';
  const isReview = task.taskType === 'TRANSCRIPTION_REVIEW';
  const isSupport = task.taskType === 'SUPPORT';
  const isIntakeReview = task.taskType === 'INTAKE_REVIEW';
  const isReleaseNotes = task.taskType === 'RELEASE_NOTES';
  const isDocument = task.taskType === 'DOCUMENT_READY';
  const isPractoSupportAssignee = task.assignedToUserId === 'PRACTO_SUPPORT';
  const effectiveStatus = optimisticDone ? 'DONE' : task.status;
  const isDone = effectiveStatus === 'DONE';
  const hasUnread = Array.isArray(task.unreadByUserIds)
    && currentUserId
    && task.unreadByUserIds.includes(String(currentUserId));
  // The backend flips status NEW → IN_PROGRESS the moment any comment lands.
  // So `unread + NEW` = brand-new task you haven't seen; `unread + anything else`
  // = task you (probably) have seen and there's new comment activity since.
  const hasNewComment = hasUnread && task.status !== 'NEW';
  const commentCount = typeof task.commentCount === 'number' ? task.commentCount : 0;

  const onToggleDone = async (e) => {
    e.stopPropagation();
    if (completing) return;
    setCompleting(true);
    try {
      if (isDone) {
        await patchTaskStatus(task._id, 'IN_PROGRESS');
        setOptimisticDone(false);
        onChanged?.();
      } else {
        // Capture the source rect BEFORE flipping optimistic state so the fly
        // animation starts from the checkbox's current position, then trigger
        // the fly-to-Done effect. Done before any await so the user sees the
        // animation react instantly to their click. The pill is detached to
        // document.body, so the row is free to unmount immediately afterwards.
        const sourceRect = checkboxRef.current?.getBoundingClientRect();
        const targetEl = getDoneButtonEl?.();
        if (sourceRect && targetEl) flyToDone(sourceRect, targetEl);

        setOptimisticDone(true);
        await patchTaskStatus(task._id, 'DONE');
        onChanged?.();
      }
    } catch (_err) {
      // Roll back optimistic state on failure — server is source of truth.
      setOptimisticDone(false);
      // RELEASE_NOTES completion is rejected until the user has viewed the
      // notes — open the detail modal so they see why and get the
      // "View Release Notes" button instead of a silently un-ticking box.
      if (isReleaseNotes) onOpen?.(task);
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      className="task-list-item p-2 rounded border-bottom"
      role="button"
      onClick={() => onOpen?.(task)}
      style={{
        cursor: 'pointer',
        background: hasUnread ? '#f0f8ff' : 'transparent',
      }}
    >
      <Stack direction="horizontal" gap={2} className="align-items-start">
        <div className="pt-1">
          {isReview && <i className="fa-light fa-microphone-lines text-primary" title="Transcription review" />}
          {isAi && <i className="fa-light fa-sparkles text-warning" title="AI-suggested" />}
          {isSupport && <i className="fa-light fa-life-ring text-primary" title="Support ticket" />}
          {isIntakeReview && <i className="fa-light fa-file-signature text-primary" title="Intake review" />}
          {isReleaseNotes && <i className="fa-light fa-bullhorn text-primary" title="Release notes" />}
          {isDocument && <i className="fa-light fa-file-lines text-primary" title="Document ready" />}
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
            {isSupport && task.jiraStatus ? (
              <Badge {...jiraStatusBadgeProps(task.jiraStatus, effectiveStatus)} pill title="JIRA status">
                {task.jiraStatus}
              </Badge>
            ) : (
              <Badge bg={statusVariant(effectiveStatus)} pill>{statusLabel(effectiveStatus)}</Badge>
            )}
            {isSupport && task.jiraIssueKey && (
              <Badge bg="light" text="dark" pill className="border" title="JIRA issue key">
                {task.jiraIssueKey}
              </Badge>
            )}
            {isSupport && task.priority && (
              <Badge bg="" pill title="Priority" {...priorityBadgeProps(task.priority)}>
                {task.priority}
              </Badge>
            )}
            {isAi && <Badge bg="warning" text="dark" pill>AI-suggested</Badge>}
            {isDocument && <Badge bg="info" text="dark" pill>Document ready</Badge>}
            {hasUnread && !isDone && (
              <span
                className="d-inline-block rounded-circle bg-danger"
                style={{ width: 8, height: 8 }}
                title="New activity"
              />
            )}
          </div>
          <div
            className="small text-body text-truncate"
            // Single-line truncation with an ellipsis. Native title attr
            // surfaces the full text on hover; clicking the row still opens
            // the detail modal where the description renders in full.
            title={task.description}
            style={{
              textDecoration: isDone ? 'line-through' : 'none',
              opacity: isDone ? 0.65 : 1,
            }}
          >
            {task.description}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end ps-2" style={{ whiteSpace: 'nowrap' }}>
          <div className="text-end small text-muted">
            {relativeTime(task.lastActivityAt || task.createdAt)}
          </div>
          {/* Quick complete checkbox — stops propagation so the row click
              doesn't open the modal. Toggling DONE off reverts to IN_PROGRESS. */}
          <Form.Check
            ref={checkboxRef}
            type="checkbox"
            id={`task-done-${task._id}`}
            checked={isDone}
            disabled={completing}
            onClick={(e) => e.stopPropagation()}
            onChange={onToggleDone}
            title={isDone ? 'Reopen task' : 'Mark as done'}
            className="mt-2"
          />
        </div>
      </Stack>
      {/* Footer row spanning the full item width: patient + practitioner on
          the left, comment count flush against the row's right edge. */}
      <div className="small text-muted mt-1 d-flex align-items-center">
        <div className="flex-grow-1 min-w-0">
          {task.patientName && <span><i className="fa-light fa-user-injured me-1" />{task.patientName}</span>}
          {task.patientName && task.assignedToName && <span className="mx-2">·</span>}
          {task.assignedToName && (
            <span>
              <i className={`fa-light ${isPractoSupportAssignee ? 'fa-life-ring' : 'fa-user'} me-1`} />
              {task.assignedToName}
            </span>
          )}
        </div>
        <div
          className="ms-2"
          style={{
            whiteSpace: 'nowrap',
            fontWeight: hasNewComment ? 700 : 400,
            color: hasNewComment ? '#0f3490' : undefined,
          }}
        >
          <i className={`fa-${hasNewComment ? 'solid' : 'light'} fa-comment me-1`} />
          <span>{commentCount} {commentCount === 1 ? 'Comment' : 'Comments'}</span>
        </div>
      </div>
    </div>
  );
}
